import { useState, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Custom hook for capturing GPS location with reverse geocoding
 * Returns coordinates, address, accuracy, and timestamp
 */
const useGeolocation = () => {
    const [location, setLocation] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [permissionDenied, setPermissionDenied] = useState(false);

    /**
     * Reverse geocode coordinates to get address
     */
    const reverseGeocode = async (lat, lng) => {
        try {
            // Use a free reverse geocoding service (Nominatim)
            const response = await axios.get(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
                {
                    headers: {
                        'Accept-Language': 'en',
                        'User-Agent': 'NextGenTournamentsApp/1.0'
                    }
                }
            );
            
            if (response.data && response.data.address) {
                const addr = response.data.address;
                return {
                    formatted: response.data.display_name,
                    city: addr.city || addr.town || addr.village || addr.county || '',
                    state: addr.state || addr.region || '',
                    country: addr.country || '',
                    country_code: addr.country_code?.toUpperCase() || '',
                    postcode: addr.postcode || ''
                };
            }
            return null;
        } catch (err) {
            console.warn('Reverse geocoding failed:', err);
            return null;
        }
    };

    /**
     * Request location permission and capture GPS coordinates
     */
    const requestLocation = useCallback(async () => {
        if (!navigator.geolocation) {
            setError('Geolocation is not supported by your browser');
            return null;
        }

        setLoading(true);
        setError(null);
        setPermissionDenied(false);

        return new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude, accuracy } = position.coords;
                    const timestamp = new Date().toISOString();
                    
                    // Reverse geocode to get address
                    const address = await reverseGeocode(latitude, longitude);
                    
                    const locationData = {
                        latitude,
                        longitude,
                        accuracy: Math.round(accuracy),
                        timestamp,
                        address: address?.formatted || '',
                        city: address?.city || '',
                        state: address?.state || '',
                        country: address?.country || '',
                        country_code: address?.country_code || ''
                    };
                    
                    setLocation(locationData);
                    setLoading(false);
                    resolve(locationData);
                },
                (err) => {
                    setLoading(false);
                    
                    switch (err.code) {
                        case err.PERMISSION_DENIED:
                            setPermissionDenied(true);
                            setError('Location permission denied. Please enable location access in your browser settings.');
                            break;
                        case err.POSITION_UNAVAILABLE:
                            setError('Location information is unavailable. Please try again.');
                            break;
                        case err.TIMEOUT:
                            setError('Location request timed out. Please try again.');
                            break;
                        default:
                            setError('An error occurred while getting your location.');
                    }
                    resolve(null);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 15000,
                    maximumAge: 0
                }
            );
        });
    }, []);

    /**
     * Clear stored location
     */
    const clearLocation = useCallback(() => {
        setLocation(null);
        setError(null);
        setPermissionDenied(false);
    }, []);

    return {
        location,
        loading,
        error,
        permissionDenied,
        requestLocation,
        clearLocation
    };
};

export default useGeolocation;
